import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { categories, getVideosByCategory } from '@/mocks/videos';
import type { VideoItem } from '@/mocks/videos';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  movie: {
    title: '免费电影高清在线观看 - 最新电影大全 | 西瓜视频',
    description: '西瓜视频免费提供最新电影在线观看，涵盖动作片、喜剧片、科幻片、爱情片等各类高清电影资源。无需会员，加载流畅不卡顿，每日更新最新大片。',
    keywords: '免费电影,高清电影,最新电影,在线看电影,动作片,喜剧片,科幻片',
  },
  drama: {
    title: '电视剧免费在线观看 - 最新电视剧全集 | 西瓜视频',
    description: '西瓜视频免费提供最新电视剧在线观看，古装剧、现代剧、悬疑剧等各类高清电视剧资源一网打尽。追剧不等待，每日更新最新剧集。',
    keywords: '免费电视剧,最新电视剧,高清电视剧,在线看电视剧,古装剧,现代剧,追剧',
  },
  variety: {
    title: '综艺节目免费在线观看 - 热门综艺大全 | 西瓜视频',
    description: '西瓜视频免费提供最新综艺节目在线观看，真人秀、脱口秀、音乐综艺等热门综艺每日更新。轻松娱乐，尽在西瓜视频。',
    keywords: '免费综艺,热门综艺,综艺节目,真人秀,脱口秀,在线看综艺',
  },
  anime: {
    title: '动漫免费在线观看 - 最新国漫日漫大全 | 西瓜视频',
    description: '西瓜视频免费提供最新动漫在线观看，国漫、日漫、热血、玄幻等各种类型动漫一网打尽。高清画质，流畅播放。',
    keywords: '免费动漫,国漫,日漫,在线看动漫,热血动漫,玄幻动漫',
  },
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'movie');
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('popular');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    const result = getVideosByCategory(activeSlug);
    const sorted = [...result].sort((a, b) => {
      if (sortBy === 'latest') return b.year - a.year;
      return b.views - a.views;
    });
    setVideos(sorted);
  }, [activeSlug, sortBy]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const meta = categoryMeta[activeSlug] || categoryMeta.movie;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory?.name || '分类' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="mb-6">
            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {meta.title.split('|')[0].trim()}
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl">{meta.description}</p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    window.history.replaceState(null, '', `/category/${cat.slug}`);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setSortBy('popular')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
                  sortBy === 'popular'
                    ? 'bg-secondary-100 text-secondary-800'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最受欢迎
              </button>
              <button
                onClick={() => setSortBy('latest')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer ${
                  sortBy === 'latest'
                    ? 'bg-secondary-100 text-secondary-800'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最新发布
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {videos.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-film-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无内容，敬请期待</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}